import React, { useMemo } from "react"
import { DateTimeFactory, useDateTime } from "../../modules/localization/useDateTime"
import { MoneyFactory, useMoney } from "../../modules/currency/useMoney"
import { MRT_ColumnDef, MRT_FilterFns } from "material-react-table"
import { COLUMN_SIZE } from "../../shared/constants/table/config"
import { AmountUnit, MonetaryAmount } from "../../modules/currency/currencyTypes"
import { toDineroOrUndefined } from "../../modules/currency/currencyLib"
import { AppLocalization } from "../../modules/localization/appLocalization"
import { useAppLocalization } from "../../modules/localization/components/AppLocalizationProvider"
import { StandardChip } from "../../shared/components/badges/StandardChip"
import { compare, Dinero } from "dinero.js"
import { rankings, rankItem } from "@tanstack/match-sorter-utils"
import { DateTime } from "luxon"
import { Row } from "@tanstack/react-table"
import { get } from "lodash"

export type ColumnConfig<TData extends Record<string, any>> = Partial<MRT_ColumnDef<TData>>

function sortDinero<TData extends Record<string, any>>(rowA: Row<TData>, rowB: Row<TData>, columnId: string) {
    const amountA = rowA.getValue<Dinero<number> | null | undefined>(columnId)
    const amountB = rowB.getValue<Dinero<number> | null | undefined>(columnId)

    if (!amountA && !amountB) return 0
    if (!amountA) return 1
    if (!amountB) return -1

    return compare(amountA, amountB)
}

function sortDateTime<TData extends Record<string, any>>(rowA: Row<TData>, rowB: Row<TData>, columnId: string) {
    const t1 = rowA.original[columnId] as DateTime | null | undefined
    const t2 = rowB.original[columnId] as DateTime | null | undefined

    if (!t1 && !t2) return 0
    if (!t1) return 1
    if (!t2) return -1

    return t1 < t2 ? -1 : t1 > t2 ? 1 : 0
}

class ColumnFactory<TData extends Record<string, any>> {
    readonly #dateTimeFactory
    readonly #moneyFactory
    readonly #l10n

    constructor(dateTimeFactory: DateTimeFactory, moneyFactory: MoneyFactory, l10n: AppLocalization) {
        this.#dateTimeFactory = dateTimeFactory
        this.#moneyFactory = moneyFactory
        this.#l10n = l10n
    }

    private dateSortingFn = (dateA: string | null | undefined, dateB: string | null | undefined) => {
        if ((!dateA || !dateA.trim()) && (!dateB || !dateB.trim())) return 0
        if (!dateA || dateA.trim() === "") return 1
        if (!dateB || dateB.trim() === "") return -1

        const datetimeA = this.#dateTimeFactory.fromApi(dateA)
        const datetimeB = this.#dateTimeFactory.fromApi(dateB)

        return datetimeA < datetimeB ? -1 : datetimeA > datetimeB ? 1 : 0
    }

    get dateTimeFactory() {
        return this.#dateTimeFactory
    }

    get moneyFactory() {
        return this.#moneyFactory
    }

    idColumn(columnDef: MRT_ColumnDef<TData>): MRT_ColumnDef<TData> {
        return {
            size: COLUMN_SIZE.S,
            maxSize: COLUMN_SIZE.S,
            ...columnDef,
        }
    }

    typeColumn(columnDef: MRT_ColumnDef<TData>): MRT_ColumnDef<TData> {
        return {
            size: COLUMN_SIZE.S,
            maxSize: COLUMN_SIZE.S,
            ...columnDef,
            muiTableHeadCellProps: {
                align: "center",
            },
            muiTableBodyCellProps: {
                align: "center",
            },
        }
    }

    endColumn(columnDef: MRT_ColumnDef<TData>): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            size: COLUMN_SIZE.S,
            maxSize: COLUMN_SIZE.M,
            muiTableHeadCellProps: {
                align: "right",
            },
            muiTableBodyCellProps: {
                align: "right",
            },
        }
    }

    nameColumn(columnDef: MRT_ColumnDef<TData>): MRT_ColumnDef<TData> {
        return {
            size: COLUMN_SIZE.M,
            maxSize: COLUMN_SIZE.XXXL,
            ...columnDef,
        }
    }

    shortNameColumn(columnDef: MRT_ColumnDef<TData>): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            size: COLUMN_SIZE.S,
            maxSize: COLUMN_SIZE.M,
        }
    }

    statusColumn(columnDef: MRT_ColumnDef<TData>): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            size: COLUMN_SIZE.S,
            maxSize: COLUMN_SIZE.S,
            filterVariant: "select",
            muiTableHeadCellProps: {
                align: "center",
            },
            muiTableBodyCellProps: {
                align: "center",
                style: {
                    verticalAlign: "middle",
                },
            },
        }
    }

    activeInactiveStatusColumn(
        columnDef: MRT_ColumnDef<TData>,
        accessorFn: (row: TData) => boolean,
        options?: { cellOverride?: (row: TData) => string | null },
    ): MRT_ColumnDef<TData> {
        return {
            ...this.statusColumn(columnDef),
            accessorFn: (row) => (accessorFn(row) ? "yes" : "no"),
            filterSelectOptions: [
                {
                    value: "yes",
                    text: this.#l10n.getString("active"),
                },
                {
                    value: "no",
                    text: this.#l10n.getString("inactive"),
                },
            ],
            Cell: ({ row }) => {
                if (options?.cellOverride) {
                    const override = options.cellOverride(row.original)
                    if (override !== null) {
                        return override
                    }
                }

                const isActive = accessorFn(row.original)

                return React.createElement(
                    StandardChip,
                    {
                        variant: isActive ? "success" : "grey",
                        placement: "table",
                    },
                    this.#l10n.getString(isActive ? "active" : "inactive"),
                )
            },
        }
    }

    percentageColumn(columnDef: MRT_ColumnDef<TData>): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            size: COLUMN_SIZE.S,
            maxSize: COLUMN_SIZE.S,
        }
    }

    dateColumn(
        columnDef: MRT_ColumnDef<TData>,
        accessorFn: (row: TData) => string | null | undefined,
    ): MRT_ColumnDef<TData> {
        return {
            size: COLUMN_SIZE.M,
            maxSize: COLUMN_SIZE.M,
            muiTableHeadCellProps: {
                align: "center",
            },
            muiTableBodyCellProps: {
                align: "center",
            },
            accessorFn: (row) => {
                const dateValue = accessorFn(row)
                return dateValue ? this.#dateTimeFactory.formatDate(this.#dateTimeFactory.fromApi(dateValue)) : ""
            },
            sortingFn: (rowA, rowB) => {
                const dateA = accessorFn(rowA.original)
                const dateB = accessorFn(rowB.original)
                return this.dateSortingFn(dateA, dateB)
            },
            GroupedCell: ({ cell }) => cell.getValue<string>(),
            ...columnDef,
        }
    }

    datetimeColumn(
        columnDef: MRT_ColumnDef<TData>,
        accessorFn: (row: TData) => string | null | undefined,
    ): MRT_ColumnDef<TData> {
        const colDef = this.parsedDateTimeColumn(columnDef, (row) => {
            const dateValue = accessorFn(row)
            return this.#dateTimeFactory.fromApiOrUndefined(dateValue)
        })

        return {
            ...colDef,
            size: COLUMN_SIZE.SM,
            maxSize: COLUMN_SIZE.SM,
            muiTableHeadCellProps: {
                align: "center",
            },
            muiTableBodyCellProps: {
                align: "center",
            },
            sortingFn: (rowA, rowB) => {
                const dateA = accessorFn(rowA.original)
                const dateB = accessorFn(rowB.original)
                return this.dateSortingFn(dateA, dateB)
            },
        }
    }

    datetimeCompactPeriod(
        columnDef: MRT_ColumnDef<TData>,
        accessorFnStart: (row: TData) => string | null | undefined,
        accessorFnEnd: (row: TData) => string | null | undefined,
    ): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            size: COLUMN_SIZE.L,
            maxSize: COLUMN_SIZE.L,
            muiTableHeadCellProps: {
                align: "center",
            },
            muiTableBodyCellProps: {
                align: "center",
            },
            accessorFn: (row) => {
                const dateValueStart = accessorFnStart(row)
                const dateValueEnd = accessorFnEnd(row)
                if (!dateValueStart || !dateValueEnd) return ""
                return this.#dateTimeFactory.formatCompactPeriod(dateValueStart, dateValueEnd)
            },
            sortingFn: (rowA, rowB, columnId) => {
                const dateA = rowA.original[columnId] as string | null | undefined
                const dateB = rowB.original[columnId] as string | null | undefined
                return this.dateSortingFn(dateA, dateB)
            },
        }
    }

    parsedDateTimeColumn(
        columnDef: MRT_ColumnDef<TData>,
        accessorFn: (row: TData) => DateTime | null | undefined,
    ): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            size: COLUMN_SIZE.M,
            maxSize: COLUMN_SIZE.M,
            muiTableHeadCellProps: {
                align: "center",
            },
            muiTableBodyCellProps: {
                align: "center",
            },
            accessorFn: (row) => {
                const dateValue = accessorFn(row)
                return dateValue ? this.#dateTimeFactory.formatDateTime(dateValue) : "-"
            },
            sortingFn: sortDateTime,
        }
    }

    moneyColumn(
        columnDef: MRT_ColumnDef<TData>,
        accessorFn: (row: TData) => MonetaryAmount | undefined,
        displayCurrency: boolean = false,
    ): MRT_ColumnDef<TData> {
        return {
            accessorFn: (originalRow) => toDineroOrUndefined(accessorFn(originalRow)),
            sortingFn: sortDinero,
            filterFn: (row, columnId, filterValue, addMeta) => {
                const amount = row.getValue<Dinero<number> | undefined>(columnId)
                const text = amount
                    ? displayCurrency
                        ? this.#moneyFactory.format(amount)
                        : this.#moneyFactory.formatAsNumber(amount)
                    : ""

                const itemRank = rankItem(text, filterValue as string, {
                    threshold: rankings.MATCHES,
                })

                addMeta(itemRank)
                return itemRank.passed
            },
            Cell: ({ cell }) => {
                const amount = cell.getValue<Dinero<number> | undefined>()
                if (!amount) return ""
                return displayCurrency ? this.#moneyFactory.format(amount) : this.#moneyFactory.formatAsNumber(amount)
            },
            size: COLUMN_SIZE.SM,
            maxSize: COLUMN_SIZE.M,
            muiTableHeadCellProps: {
                align: "right",
            },
            muiTableBodyCellProps: {
                align: "right",
            },
            ...columnDef,
        }
    }

    parsedMoneyColumn(
        columnDef: MRT_ColumnDef<TData>,
        accessorFn: (row: TData) => Dinero<number> | null | undefined,
        displayCurrency: boolean = false,
    ): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            accessorFn: (originalRow) => accessorFn(originalRow) ?? undefined,
            sortingFn: sortDinero,
            filterFn: (row, columnId, filterValue, addMeta) => {
                const amount = row.getValue<Dinero<number> | undefined>(columnId)
                const text = amount
                    ? displayCurrency
                        ? this.#moneyFactory.format(amount)
                        : this.#moneyFactory.formatAsNumber(amount)
                    : ""

                const itemRank = rankItem(text, filterValue as string, {
                    threshold: rankings.MATCHES,
                })

                addMeta(itemRank)
                return itemRank.passed
            },
            Cell: ({ cell }) => {
                const amount = cell.getValue<Dinero<number> | undefined>()
                if (!amount) return ""
                return displayCurrency ? this.#moneyFactory.format(amount) : this.#moneyFactory.formatAsNumber(amount)
            },
            size: COLUMN_SIZE.SM,
            maxSize: COLUMN_SIZE.M,
            muiTableHeadCellProps: {
                align: "right",
            },
            muiTableBodyCellProps: {
                align: "right",
            },
        }
    }

    moneyColumnWithUnit(
        columnDef: MRT_ColumnDef<TData>,
        accessorFn: (row: TData) => MonetaryAmount | undefined,
        unit: AmountUnit | ((row: TData) => AmountUnit),
    ): MRT_ColumnDef<TData> {
        return {
            ...columnDef,
            accessorFn: (originalRow) => {
                const amount = toDineroOrUndefined(accessorFn(originalRow))
                if (amount) {
                    const resolvedUnit = typeof unit === "function" ? unit(originalRow) : unit
                    return this.#moneyFactory.formatWithUnit(amount, resolvedUnit)
                } else {
                    return ""
                }
            },
            size: COLUMN_SIZE.SM,
            maxSize: COLUMN_SIZE.M,
            muiTableHeadCellProps: {
                align: "right",
            },
            muiTableBodyCellProps: {
                align: "right",
            },
        }
    }
}

export function useColumnFactory<TData extends Record<string, any>>(): ColumnFactory<TData> {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const { l10n } = useAppLocalization()

    return useMemo(
        () => new ColumnFactory<TData>(dateTimeFactory, moneyFactory, l10n),
        [dateTimeFactory, moneyFactory, l10n],
    )
}
